import * as React from 'react';

import { BoxProps, HBox, VBox } from '../Box';
import { Button } from '../Button';
import { dispatchChangeEvent } from '../dom-utils';

import { CloudinaryPhoto } from './CloudinaryPhoto';
import { CloundinaryUploaderOptions, UploadSuccess } from './CloudinaryUploader';
import { CloudinaryUploaderModal } from './CloudinaryUploaderModal';
import { CloudinaryImageSearchResult, showPhotoPickerModal } from './CloudinaryPhotoPicker';

export interface CloudinaryPhotoInputProps extends Omit<BoxProps, 'onChange'> {
  options?:Partial<CloundinaryUploaderOptions>;
  upload?:boolean;
  // this must be a valid react hook or a function that can be called repeatedly and return the same results
  search?:() => CloudinaryImageSearchResult[];
  src?:string;//image
  defaultImage?:string;
  // the image name in cloudinary, this is usually auto-generated
  imageId?:string;
  width:number;
  // imageWidth is alternative prop so you avoid collisions with box width
  imageWidth?:number;
  resolution?:number;
  ratio?:number;
  borderRadius?:number;
  onChange?:(event:React.ChangeEvent<CloudinaryPhotoInput>) => void;
  onUploadSuccess?:(info: UploadSuccess) => void;
}

interface State {
  image:string;
}

export class CloudinaryPhotoInput extends React.Component<CloudinaryPhotoInputProps, State> {
  static defaultProps = {
    upload: true
  }

  static fieldProps = {
    valueProperty: 'src',
    onChangeProperty: 'onChange'
  }

  constructor(props:CloudinaryPhotoInputProps) {
    super(props);
    this.state = {image: props.src};
  }

  componentDidUpdate(prevProps: Readonly<CloudinaryPhotoInputProps>, prevState: Readonly<State>): void {
    if (prevProps.src != this.props.src) {
      this.setState({image: this.props.src});
    }
  }

  get value() {
    return this.state.image;
  }

  get src() {
    return this.value;
  }
  
  render() {
    const {options, upload, search, src, defaultImage, imageId, width, imageWidth, resolution, ratio, borderRadius, onChange, onUploadSuccess, ...remaining} = this.props;

    return <VBox vItemSpace='$8' hAlign='left' {...remaining}>
      <CloudinaryPhoto src={this.value || defaultImage} ratio={ratio} width={width || imageWidth} resolution={resolution} border='solid 1px' borderColor='border' borderRadius={borderRadius}  />
      <HBox gap='20px 8px' flexWrap='wrap'>
        {this.renderUpload()}
        {this.renderBrowse()}
        {this.renderClear()}
      </HBox>
    </VBox>
  }

  renderUpload() {
    if (!this.props.upload) {
      return;
    }

    const defaults = {
      cropping: true,
      croppingAspectRatio: this.props.ratio,
      showSkipCropButton: false
    }
    const options = Object.assign({}, defaults, this.props.options);

    return <CloudinaryUploaderModal trigger={<Button kind='secondary' icon='Camera' iconPosition='left' small>Upload photo</Button>} imageId={this.props.imageId} options={options} 
      onUploadSuccess={this.onUploadSuccess} onChange={this.onChange} />
  }

  renderBrowse() {
    if (!this.props.search) {
      return;
    }

    return <Button kind='secondary' onClick={this.onBrowse} small >Browse</Button>;
  }

  renderClear() {
    if (!this.state.image) {
      return;
    }

    return <Button icon='X' kind='secondary' onClick={this.onClear} small ></Button>;
  }

  onBrowse = () => {
    const {width, resolution, ratio, borderRadius, search } = this.props;
    const modalProps = {width, resolution, ratio, borderRadius, search, image: this.state.image, onChange: this.onChange};

    showPhotoPickerModal(modalProps)
  }

  onChange = (image: string) => {
    this.setState({image});
    dispatchChangeEvent(this, image, this.props.onChange, 'src');
  }

  onUploadSuccess = async (info: UploadSuccess) => {
    this.setState({image: info.url});
    dispatchChangeEvent(this, info.url, this.props.onChange, 'src');
  }
  
  onClear = () => {
    this.setState({image: ''});
    dispatchChangeEvent(this, '', this.props.onChange, 'src');
  }
}